import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { KeyRound, Plus } from "lucide-react";
import type {
  BranchesResponse,
  CircleListResponse,
  Gender,
  UserResponse,
  UserSummary,
} from "@mubin/contracts";
import { displayPhone } from "@mubin/contracts";
import { EnrollmentActions } from "../components/enrollment-actions";
import { RoleModal } from "../components/roles";
import { StudentRecord } from "../components/student-record";
import { StudentExams } from "../components/exams";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, PageTitle, useSubmit } from "../components/ui";
import { CredentialsCard, type Credentials } from "../components/user-forms";
import { DeleteAccountModal } from "../components/former-members";
import { api, errorText, send } from "../lib/api";
import {
  enrollmentStatusLabel,
  genderLabel,
  personGenderLabel,
  roleLabel,
  scopeLabel,
  userStatusLabel,
} from "../lib/labels";
import { suggestPassword, useCan, useOrg } from "../lib/org";
import { useMe } from "../lib/session";
import { PhoneField } from "../components/phone-field";

const invalidateUsers = (qc: ReturnType<typeof useQueryClient>) =>
  Promise.all(["users", "circles", "overview"].map((k) => qc.invalidateQueries({ queryKey: [k] })));

export function UserPage() {
  const examOrg = useOrg();
  const { userId } = useParams({ from: "/app/users/$userId" });
  const { data: me } = useMe();
  const can = useCan();
  const qc = useQueryClient();
  const [modal, setModal] = useState<"edit" | "password" | "role" | "enroll" | "delete" | "teach" | null>(
    null,
  );
  const [editing, setEditing] = useState<UserSummary["roles"][number] | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const navigate = useNavigate();
  const user = useQuery({
    queryKey: ["users", me?.activeRole, "one", userId],
    queryFn: () => api<UserResponse>(`/v1/users/${userId}`),
  });
  if (user.isPending) return <Loading />;
  if (user.isError)
    return (
      <section className="card">
        <Empty title="المستخدم غير موجود أو خارج نطاقك">
          <Link to="/users">العودة إلى المستخدمين</Link>
        </Empty>
      </section>
    );
  const u = user.data.user;
  const manage = u.manageable && can.has("users.manage");
  const isStudent = u.roles.some((r) => r.role === "student");
  const hasActive = u.enrollments.some((e) => e.status === "active");

  async function setStatus(status: "active" | "suspended") {
    setActionError(null);
    try {
      await send("PATCH", `/v1/users/${u.id}`, { status });
      await invalidateUsers(qc);
    } catch (e) {
      setActionError(errorText(e));
    }
  }
  async function revoke(assignmentId: string) {
    setActionError(null);
    try {
      await send("POST", `/v1/role-assignments/${assignmentId}/revoke`, {});
      await invalidateUsers(qc);
    } catch (e) {
      setActionError(errorText(e));
    }
  }

  return (
    <>
      <PageTitle
        title={u.name}
        sub={u.roles.map((r) => roleLabel[r.role]).join("، ") || "بلا دور"}
        actions={
          manage && (
            <>
              {can.has("deletion.manage") && u.id !== me?.user.id && (
                <button className="btn btn-danger" onClick={() => setModal("delete")}>
                  حذف الحساب نهائيًا
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setModal("edit")}>
                تعديل البيانات
              </button>
              <button className="btn btn-secondary" onClick={() => setModal("password")}>
                <KeyRound size={16} strokeWidth={1.75} /> كلمة مرور جديدة
              </button>
              {u.status === "active" ? (
                <button className="btn btn-danger" onClick={() => void setStatus("suspended")}>
                  إيقاف الحساب
                </button>
              ) : (
                <button className="btn btn-primary" onClick={() => void setStatus("active")}>
                  إعادة التفعيل
                </button>
              )}
            </>
          )
        }
      />
      <ErrorNote error={actionError} />
      {modal === "teach" && <TeachModal onClose={() => setModal(null)} />}
      {modal === "delete" && (
        <DeleteAccountModal
          person={{ id: u.id, name: u.name }}
          onClose={() => setModal(null)}
          onDeleted={() => void navigate({ to: "/users" })}
        />
      )}
      <div className="grid-detail">
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <section className="card">
            <div className="card-h">
              <h2>الحساب</h2>
              <Badge tone={u.status === "active" ? "success" : "danger"}>{userStatusLabel[u.status]}</Badge>
            </div>
            <dl className="kv">
              <dt>الجوال</dt>
              <dd>
                <span className="ltr">{displayPhone(u.phone) || "—"}</span>
              </dd>
              <dt>الجنس</dt>
              <dd>{u.gender ? personGenderLabel[u.gender] : "غير محدد"}</dd>
              <dt>أُنشئ في</dt>
              <dd className="num">{u.createdAt.slice(0, 10)}</dd>
            </dl>
            {!manage && (
              <p className="muted small section">
                {u.id === me?.user.id ? "هذا حسابك؛ يعدّله من يملك صلاحية أعلى." : "للاطلاع فقط ضمن صلاحيتك."}
              </p>
            )}
          </section>
          <section className="card">
            <div className="card-h">
              <h2>الأدوار</h2>
              {can.has("users.roles") && u.id !== me?.user.id && (
                <button className="btn btn-ghost btn-sm" onClick={() => setModal("role")}>
                  <Plus size={16} strokeWidth={1.75} /> منح دور
                </button>
              )}
              {u.id === me?.user.id &&
                (me.activeRole === "org_admin" || me.activeRole === "branch_manager") &&
                !u.roles.some((r) => r.role === "teacher") && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setModal("teach")}>
                    <Plus size={16} strokeWidth={1.75} /> أضف نفسي معلمًا
                  </button>
                )}
            </div>
            {u.roles.length === 0 ? (
              <Empty title="لا أدوار" />
            ) : (
              <ul className="list">
                {u.roles.map((r) => (
                  <li key={r.assignmentId}>
                    <span>
                      <b>{r.profileName ?? roleLabel[r.role]}</b>{" "}
                      <span className="muted small">
                        {r.scopeType === "organization" && r.role !== "teacher"
                          ? "الجمعية كلها"
                          : `${scopeLabel[r.scopeType]}${r.scopeName ? `: ${r.scopeName}` : ""}`}
                      </span>
                    </span>
                    {can.has("users.roles") && u.id !== me?.user.id && (
                      <span className="row-actions">
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(r)}>
                          تعديل
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => void revoke(r.assignmentId)}>
                          سحب
                        </button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        {isStudent && (
          <section className="card">
            <div className="card-h">
              <h2>التسجيل في الحلقات</h2>
              {can.has("enrollments.manage") && !hasActive && (
                <button className="btn btn-secondary btn-sm" onClick={() => setModal("enroll")}>
                  تسجيل في حلقة
                </button>
              )}
            </div>
            {u.enrollments.length === 0 ? (
              <Empty title="غير مسجل في أي حلقة" />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>الحلقة</th>
                      <th>الحالة</th>
                      <th>الفترة</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {u.enrollments.map((e) => (
                      <tr key={e.id}>
                        <td>
                          <Link to="/circles/$circleId" params={{ circleId: e.circleId }}>
                            {e.circleName}
                          </Link>{" "}
                          <span className="muted small">· {e.branchName}</span>
                        </td>
                        <td>
                          <Badge tone={e.status === "active" ? "success" : "neutral"}>
                            {enrollmentStatusLabel[e.status]}
                          </Badge>
                        </td>
                        <td className="num small">
                          {e.startedOn}
                          {e.endedOn ? ` حتى ${e.endedOn}` : ""}
                        </td>
                        <td className="actions">
                          {can.has("enrollments.manage") && e.status === "active" && (
                            <EnrollmentActions enrollment={e} studentName={u.name} studentGender={u.gender} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
        {isStudent && u.enrollments.length > 0 && <StudentRecord studentId={u.id} />}
        {isStudent &&
          examOrg &&
          u.enrollments
            .filter((e) => e.status === "active")
            .map((e) => (
              <StudentExams
                key={e.id}
                organizationId={examOrg.id}
                enrollmentId={e.id}
                canSchedule={me?.activeRole !== "teacher" && me?.activeRole !== "student"}
              />
            ))}
      </div>
      <EditUserModal open={modal === "edit"} onClose={() => setModal(null)} user={u} />
      <PasswordModal open={modal === "password"} onClose={() => setModal(null)} user={u} />
      {modal === "role" && <RoleModal open onClose={() => setModal(null)} user={u} />}
      {editing && <RoleModal open onClose={() => setEditing(null)} user={u} grant={editing} />}
      <EnrollModal open={modal === "enroll"} onClose={() => setModal(null)} user={u} />
    </>
  );
}

function EditUserModal({ open, onClose, user }: { open: boolean; onClose: () => void; user: UserSummary }) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const blank = () => ({
    name: user.name,
    phone: user.phone ?? "",
    gender: (user.gender ?? "") as Gender | "",
  });
  const [form, setForm] = useState(blank);
  const [openedFor, setOpenedFor] = useState(false);
  if (open !== openedFor) {
    setOpenedFor(open);
    if (open) setForm(blank());
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await send("PATCH", `/v1/users/${user.id}`, {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        gender: form.gender || null,
      });
      await invalidateUsers(qc);
      onClose();
    }, errorText);
  }
  return (
    <Modal title="تعديل البيانات" open={open} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <Field id="ed-name" label="الاسم">
          <input
            id="ed-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <div className="form-row">
          <Field id="ed-phone" label="الجوال">
            <PhoneField id="ed-phone" value={form.phone} onChange={(phone) => setForm({ ...form, phone })} />
          </Field>
          <Field id="ed-gender" label="الجنس">
            <select
              id="ed-gender"
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value as Gender | "" })}
            >
              <option value="">غير محدد</option>
              <option value="male">{personGenderLabel.male}</option>
              <option value="female">{personGenderLabel.female}</option>
            </select>
          </Field>
        </div>
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || form.name.trim().length < 2}>
            {busy ? "جارٍ الحفظ…" : "حفظ"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function PasswordModal({ open, onClose, user }: { open: boolean; onClose: () => void; user: UserSummary }) {
  const { busy, error, run } = useSubmit();
  const [password, setPassword] = useState(suggestPassword);
  const [done, setDone] = useState<Credentials | null>(null);
  const [openedFor, setOpenedFor] = useState(false);
  if (open !== openedFor) {
    setOpenedFor(open);
    if (open) {
      setPassword(suggestPassword());
      setDone(null);
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await send("POST", `/v1/users/${user.id}/password`, { password });
      setDone({ name: user.name, login: displayPhone(user.phone), password });
    }, errorText);
  }
  return (
    <Modal title="كلمة مرور جديدة" open={open} onClose={onClose}>
      {done ? (
        <CredentialsCard creds={done} onClose={onClose} />
      ) : (
        <form className="form" onSubmit={submit}>
          <ErrorNote error={error} />
          <p className="muted">
            تُستبدل كلمة مرور <b>{user.name}</b> ويُسجَّل خروجه من كل الأجهزة.
          </p>
          <Field id="pw" label="كلمة المرور الجديدة" hint="8 أحرف على الأقل">
            <input id="pw" dir="ltr" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <div className="form-actions">
            <button className="btn btn-primary" disabled={busy || password.length < 8}>
              {busy ? "جارٍ الحفظ…" : "تعيين كلمة المرور"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function EnrollModal({ open, onClose, user }: { open: boolean; onClose: () => void; user: UserSummary }) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { busy, error, run } = useSubmit();
  const [circleId, setCircleId] = useState("");
  const circles = useQuery({
    queryKey: ["circles", me?.activeRole, "all"],
    queryFn: () => api<CircleListResponse>("/v1/circles"),
    enabled: open,
  });
  const choices = (circles.data?.circles ?? []).filter((c) => !c.gender || c.gender === user.gender);
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await send("POST", "/v1/enrollments", { studentId: user.id, circleId });
      await invalidateUsers(qc);
      onClose();
    }, errorText);
  }
  return (
    <Modal title={`تسجيل ${user.name} في حلقة`} open={open} onClose={onClose}>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <Field
          id="en-circle"
          label="الحلقة"
          hint={user.gender ? undefined : "الجنس غير محدد: تظهر الحلقات المختلطة فقط"}
        >
          <select id="en-circle" value={circleId} onChange={(e) => setCircleId(e.target.value)} required>
            <option value="">اختر الحلقة</option>
            {choices.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — {c.branchName}
                {c.gender ? ` (${genderLabel[c.gender]})` : ""}
              </option>
            ))}
          </select>
        </Field>
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || !circleId}>
            {busy ? "جارٍ التسجيل…" : "تسجيل"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** «أضف نفسي معلمًا»: an administrator who teaches too, in a branch they manage (owner decision 2026-11-04). */
function TeachModal({ onClose }: { onClose: () => void }) {
  const org = useOrg();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { busy, error, run } = useSubmit();
  const [branchId, setBranchId] = useState("");
  const branches = useQuery({
    queryKey: ["branches", me?.activeRole],
    queryFn: () => api<BranchesResponse>("/v1/branches"),
  });
  return (
    <Modal title="أضف نفسي معلمًا" open onClose={onClose}>
      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void run(async () => {
            await send("POST", "/v1/me/teacher", { organizationId: org!.id, branchId });
            await invalidateUsers(qc);
            await qc.invalidateQueries({ queryKey: ["me"] });
            onClose();
          }, errorText);
        }}
      >
        <ErrorNote error={error} />
        <Field
          id="t-branch"
          label="المجمع"
          hint="ثم أسند نفسك إلى الحلقة من صفحتها، وتدخل التطبيق بدور المعلم"
        >
          <select id="t-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} required>
            <option value="">اختر المجمع</option>
            {branches.data?.branches
              .filter((b) => !b.archivedAt)
              .map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
          </select>
        </Field>
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || !branchId || !org}>
            {busy ? "جارٍ الإضافة…" : "أضفني معلمًا"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
